import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleReviews } from '../../components/feature/GoogleReviews';

export default function SubirFacturaPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    observaciones: '',
    rgpdAccepted: false
  });
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhatsAppOption, setShowWhatsAppOption] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.rgpdAccepted) {
      alert('Debes aceptar la política de privacidad para continuar.');
      return;
    }

    if (!file) {
      alert('Por favor, sube tu factura (foto o PDF).');
      return;
    }

    setIsSubmitting(true);

    // Guardar borrador en localStorage
    localStorage.setItem('factura_borrador', JSON.stringify(formData));

    // Simular envío
    setTimeout(() => {
      // Guardar solicitud en localStorage
      const solicitudes = JSON.parse(localStorage.getItem('solicitudes') || '[]');
      const nuevaSolicitud = {
        id: Date.now().toString(),
        tipo: 'Factura de luz/gas',
        fecha: new Date().toISOString(),
        estado: 'Recibida',
        nombre: formData.nombre,
        telefono: formData.telefono,
        observaciones: formData.observaciones,
        archivo: file.name
      };
      
      solicitudes.push(nuevaSolicitud);
      localStorage.setItem('solicitudes', JSON.stringify(solicitudes));

      // Limpiar borrador
      localStorage.removeItem('factura_borrador');

      setIsSubmitting(false);
      
      // Mostrar confirmación
      alert('✅ Recibido. Te contactamos en menos de 24h.\n\nUn asesor revisará tu factura y te enviará un análisis personalizado con tu ahorro real.');
      
      // Redirigir a solicitudes
      navigate('/solicitudes');
    }, 1500);
  };

  const handleWhatsAppSend = () => {
    const mensaje = `Hola, quiero enviar mi factura por WhatsApp para que la reviséis.\n\nNombre: ${formData.nombre}\nTeléfono: ${formData.telefono}`;
    window.open(`https://wa.me/34696237625?text=${encodeURIComponent(mensaje)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#FF8F00] rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-upload-cloud-line text-white text-4xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-[#1F2937] mb-4">
            Descubre si estás pagando de más
          </h1>
          <p className="text-lg text-gray-600">
            Sube tu factura y un asesor la revisa personalmente. Si no puedes ahorrar, te lo diremos con total honestidad.
          </p>
        </div>

        {/* Google Reviews - Before Form */}
        <div className="mb-8">
          <GoogleReviews maxReviews={3} compact={true} />
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-semibold text-[#1F2937] mb-2">
              Nombre completo *
            </label>
            <input
              type="text"
              required
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors"
              placeholder="Tu nombre"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1F2937] mb-2">
              Teléfono *
            </label>
            <input
              type="tel"
              required
              value={formData.telefono}
              onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors"
              placeholder="600 000 000"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1F2937] mb-2">
              Subir factura (foto o PDF) *
            </label>
            <div className="relative">
              <input
                type="file"
                required
                accept="image/*,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center gap-3 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#0A5FFF] transition-colors cursor-pointer bg-gray-50"
              >
                <i className="ri-file-upload-line text-3xl text-gray-400"></i>
                <div className="text-center">
                  <p className="font-semibold text-[#1F2937]">
                    {file ? file.name : 'Haz clic para subir tu factura'}
                  </p>
                  <p className="text-sm text-gray-500">JPG, PNG o PDF (máx. 10MB)</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1F2937] mb-2">
              Observaciones (opcional)
            </label>
            <textarea
              value={formData.observaciones}
              onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors resize-none"
              placeholder="Cuéntanos cualquier detalle que consideres importante..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.observaciones.length}/500 caracteres
            </p>
          </div>

          {/* RGPD Checkbox */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={formData.rgpdAccepted}
                onChange={(e) => setFormData({ ...formData, rgpdAccepted: e.target.checked })}
                className="mt-1 w-5 h-5 text-[#0A5FFF] border-gray-300 rounded focus:ring-[#0A5FFF] cursor-pointer"
              />
              <span className="text-sm text-gray-700">
                ✅ Acepto la{' '}
                <a
                  href="/politica-privacidad"
                  target="_blank"
                  className="text-[#0A5FFF] hover:underline font-semibold"
                >
                  política de privacidad
                </a>{' '}
                y autorizo el análisis de mi factura. *
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#FF8F00] hover:bg-[#E68000] disabled:bg-gray-300 text-white font-bold py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                Enviando...
              </>
            ) : (
              <>
                🚀 Quiero pagar menos
              </>
            )}
          </button>

          {/* WhatsApp Alternative */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowWhatsAppOption(!showWhatsAppOption)}
              className="text-[#0A5FFF] hover:underline text-sm font-medium cursor-pointer"
            >
              Prefiero enviarla por WhatsApp
            </button>
          </div>

          {showWhatsAppOption && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-3">
                También puedes enviarnos tu factura directamente por WhatsApp:
              </p>
              <button
                type="button"
                onClick={handleWhatsAppSend}
                className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-3 rounded-full transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Enviar por WhatsApp
              </button>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center">
            * Campos obligatorios. Te responderemos en menos de 24 horas.
          </p>
        </form>

        {/* Trust badges */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-[#00C853] rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-check-line text-white text-xl"></i>
            </div>
            <p className="text-xs text-gray-600 font-medium">100% Seguro</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-[#0A5FFF] rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-time-line text-white text-xl"></i>
            </div>
            <p className="text-xs text-gray-600 font-medium">Respuesta 24h</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-[#FF8F00] rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-gift-line text-white text-xl"></i>
            </div>
            <p className="text-xs text-gray-600 font-medium">Sin compromiso</p>
          </div>
        </div>
      </div>
    </div>
  );
}
